import { Component, HostListener, OnInit, AfterViewInit, ElementRef, ViewChild, Renderer2, Inject, OnDestroy } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface Video {
  id: number;
  title: string;
  thumbnail: string;
  videoUrl: string;
  safeUrl?: SafeResourceUrl;
}

@Component({
  selector: 'app-learn-with-kalpita',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './learn-with-kalpita.component.html',
  styleUrl: './learn-with-kalpita.component.scss'
})
export class LearnWithKalpitaComponent implements OnInit, AfterViewInit, OnDestroy {
  private jsonLdScript: any;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }

  private slide(direction: 'prev' | 'next', total: number, current: number): number {
    if (this.visibleCount === 1) {
      // Mobile → move one by one
      return direction === 'next'
        ? Math.min(current + 1, total - 1)
        : Math.max(current - 1, 0);
    }

    // Desktop → page-based sliding
    const starts = this.getPageStarts(total);
    const idx = this.getCurrentPageIndex(starts, current);
    const newIdx =
      direction === 'prev'
        ? Math.max(0, idx - 1)
        : Math.min(starts.length - 1, idx + 1);

    return starts[newIdx];
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
  }
  private readonly VIDEOS_PER_SLIDE = 3;

  chunkVideos(videos: Video[]): Video[][] {
    const chunks: Video[][] = [];
    for (let i = 0; i < videos.length; i += this.VIDEOS_PER_SLIDE) {
      chunks.push(videos.slice(i, i + this.VIDEOS_PER_SLIDE));
    }
    return chunks;
  }

  shouldShowDots(videos: Video[]): boolean {
    return videos.length > this.VIDEOS_PER_SLIDE;
  }

  // Tech Talk Videos
  techTalkVideos: Video[] = [
    { id: 1, title: 'Azure Cognitive Search', thumbnail: 'https://img.youtube.com/vi/DsQTBye3TGI/maxresdefault.jpg', videoUrl: 'https://youtu.be/DsQTBye3TGI?si=TJHJt4mYKiO4WSEh' },
    { id: 2, title: 'Shift Left Testing', thumbnail: 'https://img.youtube.com/vi/SiFbchIi6Y0/maxresdefault.jpg', videoUrl: 'https://youtu.be/SiFbchIi6Y0?si=DoXLyh1fdsr6ckzL' },
    { id: 3, title: 'Microservices vs Monoliths', thumbnail: 'https://img.youtube.com/vi/t8WCfOnN0-U/maxresdefault.jpg', videoUrl: 'https://youtu.be/t8WCfOnN0-U?si=p8PgDQQKwLdsz_Nd' }
  ];

  // Training Videos
  trainingVideos: Video[] = [
    { id: 1, title: 'Azure Training', thumbnail: 'https://img.youtube.com/vi/CwTZfj1W4B0/maxresdefault.jpg', videoUrl: 'https://youtu.be/CwTZfj1W4B0?si=psfXSRUEoScwM6rx' },
    { id: 2, title: 'Dot NET 6.0 Training', thumbnail: 'https://img.youtube.com/vi/R4g4_l2N18E/hqdefault.jpg', videoUrl: 'https://youtu.be/R4g4_l2N18E?si=YIy08J--6H9yLoeR' },
    { id: 3, title: 'React JS - Redux Training', thumbnail: 'https://img.youtube.com/vi/IpMJayuoDio/mqdefault.jpg', videoUrl: 'https://youtu.be/IpMJayuoDio?si=rZRULWnf00ixF0dc' },
    { id: 4, title: 'Angular Training', thumbnail: 'https://img.youtube.com/vi/-ldTIPuRKR0/default.jpg', videoUrl: 'https://youtu.be/-ldTIPuRKR0?si=16TRZ3uE8R1YwAKQ' },
  ];

  // Brown Bag Videos
  brownbagVideos: Video[] = [
    { id: 1, title: 'Stock Market Basics', thumbnail: 'https://img.youtube.com/vi/qb2ozXVL8rU/maxresdefault.jpg', videoUrl: 'https://youtu.be/qb2ozXVL8rU?si=jdt1Fhk7mAr13X6M' },
    { id: 2, title: 'AI, ML and Applications', thumbnail: 'https://img.youtube.com/vi/bE_4SIZ-DbPNZo/hqdefault.jpg', videoUrl: 'https://youtu.be/4SIZ-DbPNZo?si=7eD4fQVO4czZxzcH' },
    { id: 3, title: 'SEO in Digital Marketing', thumbnail: 'https://img.youtube.com/vi/MxEQNXh_3dw/maxresdefault.jpg', videoUrl: 'https://youtu.be/MxEQNXh_3dw?si=QA4p3GRoQhfuuLXY' }
  ];

  // Coding Tutorial Videos
  codingtutorialVideos: Video[] = [
    { id: 1, title: 'SASS Fundamentals Explained', thumbnail: 'https://img.youtube.com/vi/jKPWQ8T1SEs/maxresdefault.jpg', videoUrl: 'https://youtu.be/jKPWQ8T1SEs?si=2gLRDDBBBoysneYZ' },
    { id: 2, title: 'Centering HTML Content', thumbnail: 'https://img.youtube.com/vi/MsffAwEVDNI/maxresdefault.jpg', videoUrl: 'https://youtu.be/MsffAwEVDNI?si=4JSfuh1B220JjdBk' },
    { id: 3, title: 'Sticky Footer Layout', thumbnail: 'https://img.youtube.com/vi/Ss7VVQgg_uA/maxresdefault.jpg', videoUrl: 'https://youtu.be/Ss7VVQgg_uA?si=KqpPLwLiF6XdwxXt' },
  ];

  // Section States
  techTalkCurrentCenterIndex = 0;
  trainingCurrentCenterIndex = 0;
  brownbagCurrentCenterIndex = 0;
  codingtutorialCurrentCenterIndex = 0;

  visibleCount = this.VIDEOS_PER_SLIDE;
  showVideoModal = false;
  currentModalVideo: Video | null = null;

  constructor(private sanitizer: DomSanitizer, private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document) { }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.processVideoUrls();
    this.updateVisibleCount();
    window.addEventListener('resize', () => this.updateVisibleCount());
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    window.removeEventListener('resize', () => this.updateVisibleCount());
  }
  injectSchemaMarkup(): void {
    const learnWithKalpitaSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita",
          "url": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita",
          "name": "Learn With Kalpita — Kalpita Technologies",
          "description": "Kalpita Technologies' learning hub featuring curated video content across four categories: Tech Talk sessions on Azure Cognitive Search, Shift Left Testing, and Microservices; Training on Azure, .NET 6.0, React JS Redux, and Angular; Brown Bag sessions on AI/ML, SEO, and stock market; and Coding Tutorials on SASS, HTML centering, and CSS layouts.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".learn-heading",
              ".learn-subheading",
              ".section-title"
            ]
          },
          "datePublished": "2024-01-01",
          "dateModified": "2025-01-01",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/",
                "url": "https://www.kalpitatechnologies.com/",
                "name": "Home"
              }
            },
            {
              "@type": "ListItem",
              "position": 2,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/gallery",
                "url": "https://www.kalpitatechnologies.com/gallery",
                "name": "Gallery"
              }
            },
            {
              "@type": "ListItem",
              "position": 3,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita",
                "url": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita",
                "name": "Learn With Kalpita"
              }
            }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Learn With Kalpita: Technology Knowledge Hub for Software Engineers and Tech Enthusiasts",
          "alternativeHeadline": "Curated Video Learning Platform Covering Azure, .NET, React, Angular, AI/ML, SASS, and Modern Software Engineering Practices",
          "backstory": "Kalpita Technologies has built a dedicated learning hub called Learn With Kalpita, offering curated video content across four key categories.",
          "proficiencyLevel": "Beginner",
          "dependencies": "Understanding of software engineering fundamentals, cloud computing (Azure), web development frameworks (.NET, React JS, Angular), frontend technologies (SASS, HTML, CSS), testing methodologies, microservices architecture, AI/ML concepts, and SEO in digital marketing",
          "articleBody": "Learn With Kalpita is Kalpita Technologies' dedicated learning and knowledge-sharing platform featuring curated video content organized across four distinct categories designed for software engineers and technology enthusiasts. The Tech Talk section features industry-focused discussions... Training section provides hands-on learning sessions... Brown Bag section offers cross-functional knowledge-sharing... and Coding Tutorial section delivers practical frontend development tutorials.",
          "keywords": [
            "Learn With Kalpita", "tech talk videos", "Azure Cognitive Search", "Shift Left Testing", "Microservices vs Monoliths", "Azure training sessions", ".NET 6.0 training", "React JS Redux training", "Angular training", "Brown Bag sessions", "AI ML applications", "SEO digital marketing", "coding tutorials", "SASS fundamentals", "HTML CSS layouts", "software engineering learning", "Kalpita Technologies education", "developer knowledge hub", "continuous learning culture", "technology video tutorials"
          ],
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
            }
          },
          "datePublished": "2024-01-01",
          "dateModified": "2025-01-01",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita"
          }
        },
        {
          "@type": "ItemList",
          "@id": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita#sections",
          "name": "Learning Categories",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "Article", "name": "Tech Talk — Azure Cognitive Search, Shift Left Testing, and Microservices", "url": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita#tech-talk" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "Article", "name": "Training — Azure, .NET 6.0, React JS Redux, and Angular", "url": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita#training" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "Article", "name": "Brown Bag — Stock Market, AI/ML Applications, and SEO", "url": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita#brown-bag" } },
            { "@type": "ListItem", "position": 4, "item": { "@type": "Article", "name": "Coding Tutorials — SASS, HTML Centering, and Sticky Footer Layout", "url": "https://www.kalpitatechnologies.com/gallery/learn-with-kalpita#coding-tutorial" } }
          ]
        },
        {
          "@type": "HowTo",
          "name": "How to Build a Developer Learning Hub Like Learn With Kalpita",
          "description": "Step-by-step guide to creating an internal knowledge-sharing platform with curated video content across tech talks, training, brown bag sessions, and coding tutorials.",
          "step": [
            { "@type": "HowToStep", "name": "Create Tech Talk Sessions", "text": "Organize tech talk sessions covering industry-relevant topics such as Azure Cognitive Search and Shift Left Testing." },
            { "@type": "HowToStep", "name": "Develop Hands-On Training", "text": "Build structured training sessions for the technology stack your team uses like .NET, React, and Angular." },
            { "@type": "HowToStep", "name": "Host Brown Bag Sessions", "text": "Encourage cross-functional learning through informal brown bag sessions on AI, ML, and digital marketing." },
            { "@type": "HowToStep", "name": "Provide Practical Coding Tutorials", "text": "Create hands-on coding tutorials for practical development skills like SASS and CSS layouts." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What is Learn With Kalpita?", "acceptedAnswer": { "@type": "Answer", "text": "Learn With Kalpita is Kalpita Technologies' dedicated learning and knowledge-sharing platform featuring curated video content across four categories." } },
            { "@type": "Question", "name": "What topics are covered in the Tech Talk section?", "acceptedAnswer": { "@type": "Answer", "text": "Industry-focused discussions including Azure Cognitive Search, Shift Left Testing, and Microservices vs Monoliths." } }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          },
          "address": [
            {
              "@type": "PostalAddress",
              "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
              "addressLocality": "Austin",
              "addressRegion": "TX",
              "postalCode": "78746",
              "addressCountry": "US"
            },
            {
              "@type": "PostalAddress",
              "streetAddress": "4M 417/A, 4th Floor, HRBR Layout 3rd Block, Kammanahalli Main Road",
              "addressLocality": "Bengaluru",
              "addressRegion": "Karnataka",
              "postalCode": "560043",
              "addressCountry": "IN"
            }
          ]
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/",
          "email": "info@kalpitatechnologies.com"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(learnWithKalpitaSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  private processVideoUrls(): void {
    const videoArrays = [
      this.techTalkVideos,
      this.trainingVideos,
      this.brownbagVideos,
      this.codingtutorialVideos
    ];

    videoArrays.forEach(videoArray => {
      videoArray.forEach(v => {
        // extract youtube id (best-effort using same logic you had)
        const id = (v.videoUrl
          .split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/)[2] || '')
          .split(/[?&]/)[0];
        v.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.youtube.com/embed/${id}?autoplay=1`
        );
      });
    });
  }

  // Tech Talk Section Methods
  get techTalkCurrentSlideVideos(): Video[] {
    return this.techTalkVideos.slice(this.techTalkCurrentCenterIndex, this.techTalkCurrentCenterIndex + this.visibleCount);
  }

  get techTalkCenterItemIndex(): number {
    return Math.floor(this.visibleCount / 2);
  }

  get techTalkDotCount(): number {
    return Math.ceil(this.techTalkVideos.length / this.visibleCount);
  }

  getTechTalkActiveDot(): number {
    return this.computeActiveDot(this.techTalkCurrentCenterIndex, this.techTalkVideos.length);
  }

  onTechTalkDotClick(dotIndex: number): void {
    const newIndex = dotIndex * this.visibleCount;
    const maxStart = Math.max(0, this.techTalkVideos.length - this.visibleCount);
    this.techTalkCurrentCenterIndex = Math.min(newIndex, maxStart);
  }

  techTalkSlide(direction: 'prev' | 'next'): void {
    this.techTalkCurrentCenterIndex = this.slide(
      direction,
      this.techTalkVideos.length,
      this.techTalkCurrentCenterIndex
    );
  }

  // Training Section Methods
  get trainingCurrentSlideVideos(): Video[] {
    return this.trainingVideos.slice(this.trainingCurrentCenterIndex, this.trainingCurrentCenterIndex + this.visibleCount);
  }

  get trainingCenterItemIndex(): number {
    return Math.floor(this.visibleCount / 2);
  }

  get trainingDotCount(): number {
    return Math.ceil(this.trainingVideos.length / this.visibleCount);
  }

  getTrainingActiveDot(): number {
    return this.computeActiveDot(this.trainingCurrentCenterIndex, this.trainingVideos.length);
  }

  onTrainingDotClick(dotIndex: number): void {
    const newIndex = dotIndex * this.visibleCount;
    const maxStart = Math.max(0, this.trainingVideos.length - this.visibleCount);
    this.trainingCurrentCenterIndex = Math.min(newIndex, maxStart);
  }

  trainingSlide(direction: 'prev' | 'next'): void {
    this.trainingCurrentCenterIndex = this.slide(
      direction,
      this.trainingVideos.length,
      this.trainingCurrentCenterIndex
    );
  }

  // Brown Bag Section Methods
  get brownbagCurrentSlideVideos(): Video[] {
    return this.brownbagVideos.slice(this.brownbagCurrentCenterIndex, this.brownbagCurrentCenterIndex + this.visibleCount);
  }

  get brownbagCenterItemIndex(): number {
    return Math.floor(this.visibleCount / 2);
  }

  get brownbagDotCount(): number {
    return Math.ceil(this.brownbagVideos.length / this.visibleCount);
  }

  getBrownbagActiveDot(): number {
    return this.computeActiveDot(this.brownbagCurrentCenterIndex, this.brownbagVideos.length);
  }

  onBrownbagDotClick(dotIndex: number): void {
    const newIndex = dotIndex * this.visibleCount;
    const maxStart = Math.max(0, this.brownbagVideos.length - this.visibleCount);
    this.brownbagCurrentCenterIndex = Math.min(newIndex, maxStart);
  }

  brownbagSlide(direction: 'prev' | 'next'): void {
    this.brownbagCurrentCenterIndex = this.slide(
      direction,
      this.brownbagVideos.length,
      this.brownbagCurrentCenterIndex
    );
  }
  // Coding Tutorial Section Methods
  get codingtutorialCurrentSlideVideos(): Video[] {
    return this.codingtutorialVideos.slice(this.codingtutorialCurrentCenterIndex, this.codingtutorialCurrentCenterIndex + this.visibleCount);
  }

  get codingtutorialCenterItemIndex(): number {
    return Math.floor(this.visibleCount / 2);
  }

  get codingtutorialDotCount(): number {
    return Math.ceil(this.codingtutorialVideos.length / this.visibleCount);
  }

  getCodingTutorialActiveDot(): number {
    return this.computeActiveDot(this.codingtutorialCurrentCenterIndex, this.codingtutorialVideos.length);
  }

  onCodingTutorialDotClick(dotIndex: number): void {
    const newIndex = dotIndex * this.visibleCount;
    const maxStart = Math.max(0, this.codingtutorialVideos.length - this.visibleCount);
    this.codingtutorialCurrentCenterIndex = Math.min(newIndex, maxStart);
  }

  codingtutorialSlide(direction: 'prev' | 'next'): void {
    this.codingtutorialCurrentCenterIndex = this.slide(
      direction,
      this.codingtutorialVideos.length,
      this.codingtutorialCurrentCenterIndex
    );
  }

  // Common Methods
  // updateVisibleCount(): void {
  //   // Set to 4 for desktop to show 4 cards in Tech Talk section
  //   if (window.innerWidth >= 1024) {
  //     this.visibleCount = 4;
  //   } else if (window.innerWidth >= 768) {
  //     this.visibleCount = 2;
  //   } else {
  //     this.visibleCount = 1;
  //   }
  // }

  updateVisibleCount(): void {
    if (window.innerWidth <= 768) {
      this.visibleCount = 1; // Mobile: one video like YouTube
    } else {
      this.visibleCount = 3; // Desktop & Tablet
    }
  }

  openVideoModal(video: Video): void {
    this.currentModalVideo = video;
    this.showVideoModal = true;
    document.body.style.overflow = 'hidden';
  }

  closeVideoModal(): void {
    this.showVideoModal = false;
    this.currentModalVideo = null;
    document.body.style.overflow = 'auto';
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.showVideoModal) this.closeVideoModal();
  }

  // Helper to compute which dot should be active (handles final partial page)
  private computeActiveDot(startIndex: number, totalItems: number): number {
    if (this.visibleCount === 1) {
      return startIndex; // Mobile → 1 dot per video
    }

    const dotCount = Math.ceil(totalItems / this.visibleCount);
    if (startIndex + this.visibleCount >= totalItems) {
      return Math.max(0, dotCount - 1);
    }
    return Math.floor(startIndex / this.visibleCount);
  }
  // Helper: returns canonical page start indices for a given total
  private getPageStarts(totalItems: number): number[] {
    const starts: number[] = [];
    const maxStart = Math.max(0, totalItems - this.visibleCount);
    for (let i = 0; i * this.visibleCount < totalItems; i++) {
      const start = Math.min(i * this.visibleCount, maxStart);
      if (starts.length === 0 || starts[starts.length - 1] !== start) {
        starts.push(start);
      }
    }
    return starts;
  }

  // Helper: find the current page index inside canonical starts (robust if current isn't exactly present)
  private getCurrentPageIndex(starts: number[], current: number): number {
    const exact = starts.indexOf(current);
    if (exact !== -1) return exact;
    // if not exact, return the nearest previous start (or first)
    for (let i = 0; i < starts.length; i++) {
      if (starts[i] > current) return Math.max(0, i - 1);
    }
    return Math.max(0, starts.length - 1);
  }

  // Safer iterable for *ngFor
  createArray(n: number): number[] {
    return Array.from({ length: Math.max(0, n) }, (_, i) => i);
  }
}
